
import { Component, Prop, Vue } from "vue-property-decorator";
import BuroPicker from "@/components/pickers/BuroPicker.vue";

/**
 * Büro silme sihirbazı.
 *
 * Üç adım: (1) özet ve engeller, (2) kişi bazlı karar, (3) onay.
 * Karar seçenekleri SUNUCUDAN gelir (`secenekler`); istemci kendi kuralını
 * uydurmaz — sunucu aynı kuralı silme anında tekrar doğrular.
 */
@Component({ components: { BuroPicker } })
export default class BuroSilSihirbaziDialog extends Vue {
  @Prop({ required: true }) buroId!: number;
  @Prop({ default: "" }) buroAdi!: string;
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ default: "" }) tooltip!: string;

  show = false;
  yukleniyor = false;
  siliniyor = false;
  adim: number = 1;
  onayVerildi = false;
  hata = "";

  engeller: Array<any> = [];
  personeller: Array<any> = [];
  zatenPasif: Array<any> = [];
  hedefBurolar: Array<any> = [];
  kasaSayisi = 0;
  planImzasi = "";
  buroBilgi: any = null;

  kararlar: { [key: number]: { karar: string; hedef_buro_id: number | null } } = {};
  topluKarar: string | null = null;
  topluHedefBuro: number | null = null;
  yoneticiHedefBuro: number | null = null;
  yoneticiTasiniyor = false;

  basliklar = [
    { text: "Adı", value: "ad", align: "start", sortable: false },
    { text: "Personel Türü", value: "personel_turu", align: "start", sortable: false },
    { text: "Karar", value: "karar", align: "start", sortable: false },
  ];

  footerProps = {
    "items-per-page-options": [5, 10, 15, -1],
    "items-per-page-all-text": "Hepsi",
    "items-per-page-text": "Sayfa başına satır:",
  };

  get buroBasligi(): string {
    const ad = this.buroBilgi?.isim || this.buroAdi || "Büro";
    return `${ad} #${this.buroId}`;
  }

  get adimAdi(): string {
    return ["Özet", "Personel Kararı", "Onay"][this.adim - 1] || "";
  }

  get topluKararSecenekleri() {
    return [
      { text: "Başka büroya taşı", value: "tasi" },
      { text: "Pasife al", value: "pasife_al" },
    ];
  }

  kararSecenekleri(item: any) {
    const etiket: any = {
      tasi: "Başka büroya taşı",
      pasife_al: "Pasife al",
      cikar: "Bu bürodan çıkar",
    };
    return (item.secenekler || []).map((s: string) => ({ text: etiket[s] || s, value: s }));
  }

  get pasifeAlinacakSayisi(): number {
    return Object.values(this.kararlar).filter((k) => k.karar === "pasife_al").length;
  }

  get tasinacakSayisi(): number {
    return Object.values(this.kararlar).filter((k) => k.karar === "tasi").length;
  }

  get cikarilacakSayisi(): number {
    return Object.values(this.kararlar).filter((k) => k.karar === "cikar").length;
  }

  get ozetSatirlari(): Array<string> {
    const satirlar: Array<string> = [];
    if (this.tasinacakSayisi) satirlar.push(`${this.tasinacakSayisi} personel başka büroya taşınacak.`);
    if (this.pasifeAlinacakSayisi) satirlar.push(`${this.pasifeAlinacakSayisi} personel pasife alınacak.`);
    if (this.cikarilacakSayisi) satirlar.push(`${this.cikarilacakSayisi} personel yalnızca bu bürodan çıkarılacak.`);
    if (this.zatenPasif.length) satirlar.push(`${this.zatenPasif.length} pasif personel tüm bürolarda görünür olacak.`);
    if (!satirlar.length) satirlar.push("Büroda personel yok; yalnızca büro silinecek.");
    return satirlar;
  }

  get onayMetni(): string {
    return this.pasifeAlinacakSayisi > 0
      ? `${this.pasifeAlinacakSayisi} personelin pasife alınacağını anladım.`
      : "Bu işlemin geri alınamayacağını anladım.";
  }

  get silButonuMetni(): string {
    return this.pasifeAlinacakSayisi > 0
      ? `Büroyu Sil ve ${this.pasifeAlinacakSayisi} Personeli Pasife Al`
      : "Büroyu Sil";
  }

  /** Her satırda karar verilmiş ve taşımalarda hedef seçilmiş olmalı. */
  get kararlarTamam(): boolean {
    return this.personeller.every((p: any) => {
      const k = this.kararlar[p.id];
      if (!k || !k.karar) return false;
      return k.karar !== "tasi" || !!k.hedef_buro_id;
    });
  }

  get ilerlemeKapali(): boolean {
    if (this.engeller.length) return true;
    if (this.adim === 2 && !this.kararlarTamam) return true;
    return false;
  }

  ac() {
    this.show = true;
    this.adim = 1;
    this.onayVerildi = false;
    this.hata = "";
    this.topluKarar = null;
    this.topluHedefBuro = null;
    this.yoneticiHedefBuro = null;
    this.onizlemeYukle();
  }

  kapat() {
    if (this.siliniyor) return;
    this.show = false;
  }

  onizlemeYukle() {
    this.yukleniyor = true;
    this.$http
      .get("/api/v1/buro/" + this.buroId + "/silme-onizleme")
      .then((res: any) => {
        const v = res.data?.data ?? res.data ?? {};
        this.buroBilgi = v.buro ?? null;
        this.engeller = v.engeller ?? [];
        this.personeller = v.personeller ?? [];
        this.zatenPasif = v.zaten_pasif ?? [];
        this.hedefBurolar = v.hedef_burolar ?? [];
        this.kasaSayisi = v.kasa_sayisi ?? 0;
        this.planImzasi = v.plan_imzasi ?? "";

        // Sunucunun akıllı varsayılanları uygulanır; tek hedef büro varsa seçilir.
        const kararlar: any = {};
        const tekHedef = this.hedefBurolar.length === 1 ? Number(this.hedefBurolar[0].id) : null;
        this.personeller.forEach((p: any) => {
          kararlar[p.id] = {
            karar: p.varsayilan_karar,
            hedef_buro_id: p.varsayilan_karar === "tasi" ? tekHedef : null,
          };
        });
        this.kararlar = kararlar;
      })
      .catch((e: any) => {
        this.hata = this.hataMesaji(e);
      })
      .finally(() => {
        this.yukleniyor = false;
      });
  }

  ilerle() {
    // Personel yoksa karar adımı atlanır.
    if (this.adim === 1 && this.personeller.length === 0) {
      this.adim = 3;
      return;
    }
    this.adim++;
  }

  topluUygula() {
    if (!this.topluKarar) return;
    this.personeller.forEach((p: any) => {
      // Kişiye sunulmayan bir karar zorla uygulanmaz (sunucu da reddeder).
      if (!(p.secenekler || []).includes(this.topluKarar)) return;
      this.kararlar[p.id].karar = this.topluKarar as string;
      this.kararlar[p.id].hedef_buro_id =
        this.topluKarar === "tasi" ? this.topluHedefBuro : null;
    });
    this.kararlar = { ...this.kararlar };
  }

  yoneticiyiTasi(engel: any) {
    if (!this.yoneticiHedefBuro) return;
    this.yoneticiTasiniyor = true;
    this.$http
      .post("/api/v1/personel/" + engel.personel_id + "/buro-degistir", {
        hedef_buro_id: this.yoneticiHedefBuro,
        kaynak_buro_id: this.buroId,
        mod: "tasi",
      })
      .then((res: any) => {
        const veri = res.data?.data ?? res.data ?? {};
        if (veri.kod) {
          this.hata = veri.message || "Yönetici taşınamadı.";
          return;
        }
        (this as any).$toast?.success("Yönetici taşındı.");
        this.$emit("load");
        this.onizlemeYukle();
      })
      .catch((e: any) => {
        this.hata = this.hataMesaji(e);
      })
      .finally(() => {
        this.yoneticiTasiniyor = false;
      });
  }

  sil() {
    if (!this.onayVerildi || this.siliniyor) return;
    this.siliniyor = true;
    this.hata = "";

    const plan = this.personeller.map((p: any) => ({
      user_id: p.id,
      karar: this.kararlar[p.id].karar,
      hedef_buro_id: this.kararlar[p.id].hedef_buro_id,
    }));

    this.$http
      .post("/api/v1/buro/" + this.buroId + "/sil", {
        plan,
        plan_imzasi: this.planImzasi,
      })
      .then((res: any) => {
        const v = res.data?.data ?? res.data ?? {};
        const ozet = v.ozet ?? {};
        (this as any).$toast?.success(
          `Büro silindi. ${ozet.tasinan ?? 0} personel taşındı, ${ozet.pasife_alinan ?? 0} personel pasife alındı.`
        );
        this.show = false;
        this.$emit("success");
        this.$emit("load");
      })
      .catch((e: any) => {
        this.hata = this.hataMesaji(e);
        // Liste değiştiyse (409 plan_bayat) önizlemeyi tazeleyip kullanıcıyı
        // 2. adıma geri al: kararlar yeniden verilmeli.
        const kod = e?.response?.data?.data?.kod;
        if (kod === "plan_bayat" || kod === "gecersiz_karar" || kod === "hedef_buro_yok") {
          this.onayVerildi = false;
          this.onizlemeYukle();
          this.adim = 2;
        }
      })
      .finally(() => {
        this.siliniyor = false;
      });
  }

  hataMesaji(e: any): string {
    const veri = e?.response?.data?.data ?? e?.response?.data ?? {};
    if (veri.message) return veri.message;
    if (Array.isArray(veri.engeller) && veri.engeller.length) return veri.engeller[0].mesaj;
    return "İşlem tamamlanamadı. Lütfen tekrar deneyin.";
  }
}
